"use client";

import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";

import { cn } from "../../utils";

type TabsVariant = "default" | "line";

const TabsListVariantContext = React.createContext<TabsVariant>("default");

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        "af-group/tabs af-flex af-gap-2 data-[orientation=horizontal]:af-flex-col",
        className,
      )}
      {...props}
    />
  );
}
Tabs.displayName = "Tabs";

const tabsListVariants = cva(
  "af-inline-flex af-items-center af-justify-center af-text-muted-foreground group-data-[orientation=horizontal]/tabs:af-h-auto group-data-[orientation=vertical]/tabs:af-h-fit group-data-[orientation=vertical]/tabs:af-flex-col",
  {
    variants: {
      variant: {
        default: "af-w-fit af-rounded-lg af-bg-muted af-p-[3px]",
        line: "af-gap-1 af-rounded-none af-border-border af-bg-transparent group-data-[orientation=horizontal]/tabs:af-w-full group-data-[orientation=horizontal]/tabs:af-justify-start group-data-[orientation=horizontal]/tabs:af-border-b group-data-[orientation=vertical]/tabs:af-border-r group-data-[orientation=horizontal]/tabs:af-py-[5px] group-data-[orientation=vertical]/tabs:af-pr-[5px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsListVariantContext.Provider value={variant ?? "default"}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      />
    </TabsListVariantContext.Provider>
  );
}
TabsList.displayName = "TabsList";

const tabsTriggerVariants = cva(
  "af-relative af-inline-flex af-flex-1 af-items-center af-justify-center af-gap-1.5 af-whitespace-nowrap af-rounded-md af-border af-border-transparent af-px-3 af-py-2 af-text-sm af-font-medium af-text-foreground/60 af-transition-all after:af-absolute after:af-bg-primary after:af-opacity-0 after:af-transition-opacity hover:af-text-foreground focus-visible:af-border-ring focus-visible:af-outline-1 focus-visible:af-outline-ring focus-visible:af-ring-[3px] focus-visible:af-ring-ring/50 disabled:af-pointer-events-none disabled:af-opacity-50 has-[[data-icon=inline-end]]:af-pr-1 has-[[data-icon=inline-start]]:af-pl-1 group-data-[orientation=vertical]/tabs:af-w-full group-data-[orientation=vertical]/tabs:af-justify-start group-data-[orientation=horizontal]/tabs:after:af-inset-x-[-1px] group-data-[orientation=vertical]/tabs:after:af-inset-y-[-1px] group-data-[orientation=horizontal]/tabs:after:af-bottom-[-7px] group-data-[orientation=vertical]/tabs:after:af-right-[-7px] group-data-[orientation=horizontal]/tabs:after:af-h-0.5 group-data-[orientation=vertical]/tabs:after:af-w-0.5 dark:af-text-muted-foreground dark:hover:af-text-foreground [&_svg:not([class*='size-'])]:af-size-4 [&_svg]:af-pointer-events-none [&_svg]:af-shrink-0 [&_svg]:af-text-muted-foreground",
  {
    variants: {
      variant: {
        default:
          "data-[state=active]:af-bg-background data-[state=active]:af-text-foreground data-[state=active]:af-shadow-sm dark:data-[state=active]:af-border-input dark:data-[state=active]:af-bg-input/30 dark:data-[state=active]:af-text-foreground",
        line: "af-border-transparent hover:af-bg-accent data-[state=active]:af-text-foreground data-[state=active]:af-shadow-none data-[state=active]:after:af-opacity-100 dark:data-[state=active]:af-border-transparent dark:data-[state=active]:af-bg-transparent [&_svg]:hover:af-text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsListVariantContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}
TabsTrigger.displayName = "TabsTrigger";

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("af-flex-1 af-text-sm af-outline-none", className)}
      {...props}
    />
  );
}
TabsContent.displayName = "TabsContent";

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  tabsListVariants,
  tabsTriggerVariants,
};
